import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { formatDate, getMonthName } from '../lib/utils';
import { Plus, Pencil, Trash2, Clock, Printer, Users, Filter, Wand2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ExportButtons } from '../components/ExportButtons';
import { SnapshotButtons } from '../components/SnapshotButtons';
import { PlanningForm } from '../components/planning/PlanningForm';
import { SyntheseHebdo } from '../components/planning/SyntheseHebdo';
import { PlanningAssistant } from '../components/planning/PlanningAssistant';
import { PrintableWeeklyPlanning } from '../components/planning/PrintableWeeklyPlanning';
import { HeuresRestantes } from '../components/planning/HeuresRestantes';

export function PlanningPage() {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [employesDetail, setEmployesDetail] = useState([]);
  const [allData, setAllData] = useState([]);
  const [month, setMonth] = useState(null);
  const [semaine, setSemaine] = useState(null);
  const [filterEmploye, setFilterEmploye] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [syntheseData, setSyntheseData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [synthFilterEmploye, setSynthFilterEmploye] = useState('all');
  const printRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (month) params.mois = month;
      if (semaine) params.semaine = semaine;
      const res = await api.get('/planning-prev', { params });
      setData(res.data);
      // Planning complet (toutes semaines) pour le calcul des heures restantes
      if (Object.keys(params).length === 0) setAllData(res.data);
      else { const all = await api.get('/planning-prev'); setAllData(all.data); }
    } catch {
      toast.error('Erreur chargement planning');
    } finally {
      setLoading(false);
    }
  }, [api, month, semaine]);

  const fetchEmployes = useCallback(async () => {
    try {
      const res = await api.get('/rh/employes');
      setEmployesDetail(res.data || []);
      const empList = res.data.map(e => e.prenom?.trim()).filter(Boolean);
      if (empList.length > 0) {
        setEmployes(empList);
      } else {
        const listRes = await api.get('/listes/employe');
        setEmployes(listRes.data.map(e => e.valeur));
      }
    } catch {
      try {
        const listRes = await api.get('/listes/employe');
        setEmployes(listRes.data.map(e => e.valeur));
      } catch {
        setEmployes([]);
      }
    }
  }, [api]);

  const fetchSynthese = useCallback(async () => {
    if (!semaine) { setSyntheseData(null); return; }
    try {
      const res = await api.get('/planning-prev/synthese-semaine', { params: { semaine } });
      setSyntheseData(res.data);
    } catch {
      console.error('Erreur synthese');
    }
  }, [api, semaine]);

  useEffect(() => { fetchData(); fetchSynthese(); }, [fetchData, fetchSynthese]);
  useEffect(() => { fetchEmployes(); }, [fetchEmployes]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/planning-prev/${id}`);
      toast.success('Supprime');
      selectedIds.delete(id);
      setSelectedIds(new Set(selectedIds));
      fetchData();
      fetchSynthese();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      await api.post('/planning-prev/delete-bulk', { ids: [...selectedIds] });
      toast.success(`${selectedIds.size} planning(s) supprime(s)`);
      setSelectedIds(new Set());
      fetchData();
      fetchSynthese();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const filteredData = filterEmploye === 'all' ? data : data.filter(d => d.employe === filterEmploye);
  const totalHeures = filteredData.reduce((sum, p) => sum + (p.total || 0), 0);
  const uniqueEmployes = [...new Set(data.map(d => d.employe))];

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredData.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredData.map(d => d.id)));
  };

  const handlePrint = () => { window.print(); };

  const getCurrentWeek = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now - start) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + start.getDay() + 1) / 7);
  };

  const formatTime = (time) => time || '-';
  const formatDuration = (hours) => hours ? `${hours.toFixed(1)}h` : '-';

  const onFormSuccess = () => { fetchData(); fetchSynthese(); };

  return (
    <div className="space-y-6">
      {/* Print template */}
      <PrintableWeeklyPlanning
        ref={printRef}
        data={data}
        semaine={semaine}
        year={new Date().getFullYear()}
        employes={uniqueEmployes}
        syntheseData={syntheseData}
      />

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .printable-planning, .printable-planning * { visibility: visible !important; display: block; }
          .printable-planning table, .printable-planning thead, .printable-planning tbody, .printable-planning tr, .printable-planning th, .printable-planning td { visibility: visible !important; display: revert; }
          .printable-planning { position: absolute; left: 0; top: 0; width: 100%; }
          .print\\:hidden, .no-print { display: none !important; }
        }
      `}</style>

      <div className="module-header">
        <div>
          <h1 className="module-title" data-testid="planning-title">Planning Previsionnel</h1>
          <p className="text-muted-foreground mt-1">Planification des horaires des employes</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <SnapshotButtons module="planning" moduleLabel="Planning" />
          <ExportButtons api={api} moduleKey="planning" />
          <Button variant="outline" onClick={handlePrint} className="gap-2 print:hidden" data-testid="print-btn">
            <Printer className="w-4 h-4" /> Imprimer
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 print:hidden" data-testid="new-planning-btn" onClick={() => setEditItem(null)}>
                <Plus className="w-4 h-4" /> Nouveau planning
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editItem ? 'Modifier' : 'Nouveau'} Planning</DialogTitle>
              </DialogHeader>
              <PlanningForm item={editItem} onClose={() => setDialogOpen(false)} employes={employes} employesDetail={employesDetail} planning={allData} api={api} onSuccess={onFormSuccess} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="planning">
        <TabsList className="print:hidden">
          <TabsTrigger value="planning" data-testid="tab-planning"><Clock className="w-4 h-4 mr-1" /> Planning</TabsTrigger>
          <TabsTrigger value="synthese" data-testid="tab-synthese"><Users className="w-4 h-4 mr-1" /> Synthese Semaine</TabsTrigger>
          <TabsTrigger value="assistant" data-testid="tab-assistant"><Wand2 className="w-4 h-4 mr-1" /> Assistant</TabsTrigger>
        </TabsList>

        <TabsContent value="planning" className="space-y-4">
          <div className="flex flex-wrap items-center gap-4 print:hidden">
            <Select value={month?.toString() || 'all'} onValueChange={(v) => setMonth(v === 'all' ? null : parseInt(v))}>
              <SelectTrigger className="w-[160px]" data-testid="filter-month"><SelectValue placeholder="Tous les mois" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les mois</SelectItem>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (<SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>))}
              </SelectContent>
            </Select>

            <Select value={semaine?.toString() || 'all'} onValueChange={(v) => setSemaine(v === 'all' ? null : parseInt(v))}>
              <SelectTrigger className="w-[160px]" data-testid="filter-semaine"><SelectValue placeholder="Toutes semaines" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes semaines</SelectItem>
                {Array.from({ length: 53 }, (_, i) => i + 1).map(s => (
                  <SelectItem key={s} value={s.toString()}>{s === getCurrentWeek() ? `Semaine ${s} (actuelle)` : `Semaine ${s}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterEmploye} onValueChange={setFilterEmploye}>
              <SelectTrigger className="w-[180px]" data-testid="filter-employe">
                <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Tous les employes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les employes</SelectItem>
                {uniqueEmployes.map(e => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
              </SelectContent>
            </Select>

            <div className="ml-auto flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
              <Clock className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Total:</span>
              <span className="text-lg font-bold text-primary tabular-nums">{totalHeures.toFixed(1)}h</span>
              {filterEmploye !== 'all' && (<Badge variant="secondary" className="ml-1">{filterEmploye}</Badge>)}
            </div>
          </div>

          <HeuresRestantes planning={allData} employesDetail={employesDetail} semaine={semaine} month={month} onSelectSemaine={(s) => setSemaine(s)} />

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg print:hidden" data-testid="bulk-delete-bar">
              <span className="text-sm font-medium">{selectedIds.size} planning(s) selectionne(s)</span>
              <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="deselect-all-btn">
                Deselectionner
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="gap-1" data-testid="bulk-delete-btn">
                    <Trash2 className="w-4 h-4" /> Supprimer la selection
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer {selectedIds.size} planning(s) ?</AlertDialogTitle>
                    <AlertDialogDescription>Cette action est irreversible.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteSelected}>Supprimer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px] print:hidden">
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                        checked={filteredData.length > 0 && selectedIds.size === filteredData.length}
                        onChange={toggleSelectAll} data-testid="select-all-checkbox" />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Semaine</TableHead>
                    <TableHead>Employe</TableHead>
                    <TableHead className="text-center">Midi</TableHead>
                    <TableHead className="text-center">Soir</TableHead>
                    <TableHead className="text-right">Duree Midi</TableHead>
                    <TableHead className="text-right">Duree Soir</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-[100px] print:hidden"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                        Aucun planning
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map(item => (
                      <TableRow key={item.id} className={selectedIds.has(item.id) ? 'bg-primary/5' : ''}>
                        <TableCell className="print:hidden">
                          <input type="checkbox" className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                            checked={selectedIds.has(item.id)} onChange={() => toggleSelect(item.id)}
                            data-testid={`select-row-${item.id}`} />
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground">{format(new Date(item.date + 'T00:00:00'), 'EEEE', { locale: fr })}</span>{' '}
                          {formatDate(item.date)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">S{item.semaine_iso}</TableCell>
                        <TableCell className="font-medium">{item.employe}</TableCell>
                        <TableCell className="text-center text-sm">
                          {item.debut_midi && item.fin_midi ? `${formatTime(item.debut_midi)} - ${formatTime(item.fin_midi)}` : '-'}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {item.debut_soir && item.fin_soir ? `${formatTime(item.debut_soir)} - ${formatTime(item.fin_soir)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatDuration(item.duree_midi)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatDuration(item.duree_soir)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{formatDuration(item.total)}</TableCell>
                        <TableCell className="print:hidden">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setDialogOpen(true); }}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer ?</AlertDialogTitle>
                                  <AlertDialogDescription>Cette action est irreversible.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(item.id)}>Supprimer</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="synthese" className="space-y-4">
          <div className="flex items-center gap-4 print:hidden">
            <Select value={semaine?.toString() || 'all'} onValueChange={(v) => setSemaine(v === 'all' ? null : parseInt(v))}>
              <SelectTrigger className="w-[200px]" data-testid="synthese-semaine-filter"><SelectValue placeholder="Selectionner une semaine" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Choisir une semaine</SelectItem>
                {Array.from({ length: 53 }, (_, i) => i + 1).map(s => (
                  <SelectItem key={s} value={s.toString()}>{s === getCurrentWeek() ? `Semaine ${s} (actuelle)` : `Semaine ${s}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <SyntheseHebdo
            syntheseData={syntheseData}
            semaine={semaine}
            synthFilterEmploye={synthFilterEmploye}
            setSynthFilterEmploye={setSynthFilterEmploye}
            printRef={printRef}
            onPrint={handlePrint}
          />
        </TabsContent>

        <TabsContent value="assistant" className="space-y-6">
          <PlanningAssistant api={api} semaine={semaine} setSemaine={setSemaine} fetchData={fetchData} fetchSynthese={fetchSynthese} getCurrentWeek={getCurrentWeek} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
